import { FormikProps, useFormikContext } from 'formik'
import { useEffect, useRef } from 'react'
import { Installments, ScheduleType } from '../../../types/flyout'
import {
  buildCustomRenewalSync,
  buildRenewalRowsFromMainInstallments,
  dayFromIsoDate,
} from './renewalSchedule'

type CustomRenewalSyncOptions = {
  renewalInstallments: Installments[]
  scheduleTypeField: string
  installmentsField: string
  dayOfMonthField: string
  dayOfMonth: number
  currency: string
  tcv: number
}

export const useCustomRenewalSync = ({
  renewalInstallments,
  scheduleTypeField,
  installmentsField,
  dayOfMonthField,
  dayOfMonth,
  currency,
  tcv,
}: CustomRenewalSyncOptions) => {
  const { values, setFieldValue } = useFormikContext() as FormikProps<
    Record<string, unknown>
  >
  const previousSignatureRef = useRef<string | null>(null)
  const initialInvoiceDateRef = useRef<string | undefined>(undefined)
  const initializedFromMainRef = useRef(false)

  useEffect(() => {
    if (initializedFromMainRef.current) return
    const mainInstallments = (values.installments as Installments[]) || []
    if (mainInstallments.length === 0) return

    if (renewalInstallments.length > 0) {
      initializedFromMainRef.current = true
      return
    }

    const mainSchedule = values.flexiblePaymentType as ScheduleType | undefined
    if (!mainSchedule || mainSchedule === ScheduleType.CUSTOM) return

    initializedFromMainRef.current = true
    const renewalRows = buildRenewalRowsFromMainInstallments({
      mainInstallments,
      scheduleType: mainSchedule,
      contractStartDate: values.agreementStartDate as string | undefined,
    })
    void setFieldValue(scheduleTypeField, mainSchedule, false)
    void setFieldValue(
      dayOfMonthField,
      dayFromIsoDate(mainInstallments[0]?.paymentDate) || dayOfMonth,
      false
    )
    void setFieldValue(installmentsField, renewalRows, false)
  }, [
    dayOfMonth,
    dayOfMonthField,
    installmentsField,
    renewalInstallments,
    scheduleTypeField,
    setFieldValue,
    values,
  ])

  useEffect(() => {
    const mainInstallments = (values.installments as Installments[]) || []
    if (!initialInvoiceDateRef.current && mainInstallments[0]?.paymentDate) {
      initialInvoiceDateRef.current = mainInstallments[0].paymentDate
    }

    const signature = JSON.stringify([
      values.flexiblePaymentType,
      values.agreementStartDate,
      mainInstallments.map(row => [row.paymentDate, row.amount]),
    ])
    if (signature === previousSignatureRef.current) return
    const isInitialLoad = previousSignatureRef.current === null
    previousSignatureRef.current = signature

    if (values.flexiblePaymentType !== ScheduleType.CUSTOM) return
    // Opening a draft must not replace independently edited renewal terms.
    if (isInitialLoad && renewalInstallments.length > 0) return
    if (mainInstallments.length === 0) return

    const syncedRenewal = buildCustomRenewalSync({
      mainInstallments,
      renewalInstallments,
      contractStartDate: values.agreementStartDate as string | undefined,
      firstInstallmentDate: values.firstInstallmentDate as string | null,
      initialInvoiceDate: initialInvoiceDateRef.current,
      dayOfMonth,
      currency,
      tcv,
    })
    void setFieldValue(scheduleTypeField, ScheduleType.CUSTOM, false)
    void setFieldValue(dayOfMonthField, syncedRenewal.dayOfMonth, false)
    void setFieldValue(installmentsField, syncedRenewal.installments, false)
  }, [
    currency,
    dayOfMonth,
    dayOfMonthField,
    installmentsField,
    renewalInstallments,
    scheduleTypeField,
    setFieldValue,
    tcv,
    values,
  ])
}
